import { JSX, ReactNode, createContext, useContext, useState } from 'react';
import * as vp from 'vopay';

export type ClientData = {
  Success: boolean | undefined;
  LoginUserName: string;
  AccountID: string;
  AccountName: string;
  Key: string;
  Signature: string;
  JWT: string;
  FirebaseToken: string;
  Country: string;
  GCMEnabled: string;
  EFTCollectEnabled: string;
  EFTSendEnabled: string;
  ACHFundEnabled: string;
  ACHWithdrawEnabled: string;
  USDEFTCollectEnabled: string;
  USDEFTSendEnabled: string;
  CADCreditCardCollectionEnabled: boolean;
  USDCreditCardCollectionEnabled: boolean;
  RTRWithdrawEnabled: string;
  RTPWithdrawEnabled: string;
  WireEnabled: string;
  AFTEnabled: boolean;
  InteracMoneyRequestEnabled: string;
  InboundEtransferEnabled: string;
  InteracBulkPayoutEnabled: string;
  PortalViewOnly: string;
  MoneyRequestGenerateUrlEnabled: string;
  SubAccountPermissions: string;
  subAccountPermissions: boolean;
  ClientAccountsEnabled: string;
  clientAccountsEnabled: boolean;
  AutoBalanceTransferEnabled: string;
  TransactionLabelOverrideEnabled: string;
  TransactionResubmissionEnabled: string;
  IQ11StandardEnabled: string;
  IQ11BoostEnabled: string;
  IQ11PlusEnabled: string;
  IQ11AdvantageEnabled: string;
  IQ11DataEnrichingEnabled: boolean;
  IQ11DataEnrichingEnhancedEnabled: boolean;
  TransactionServiceDefault: string;
  PayLinkEnabled: string;
  CustomFlinksCredentialsSet: boolean | undefined;
  CustomPlaidCredentialsSet: boolean | undefined;
  CustomInveriteCredentialsSet: boolean | undefined;
  EnablePaylinkDecline: string;
  CreditCardEnabled: string;
  GooglePayEnabled: boolean;
  ApplePayEnabled: boolean;
  UserManagementEnabled: string;
  AccountingSoftwareIntegrationEnabled: boolean;
  AccountingSystemIntegrationEnabled: string;
  AccountTransferEnabled: string;
  PushToCardEnabled: string;
  RealTimeMoneyRequestEnabled: string;
  CustomGlobalPaySharedSecret: boolean | undefined;
  KYCLightEnabled: string;
  AccountType: vp.ClientPortalAccountType;
  Domain: string;
  MFAEnabled: boolean | undefined;
  PasswordResetRequired: boolean | undefined;
  BrandingEmailEnabled: string;
  BrandingELinxEnabled: string;
  BrandingPortalEnabled: string;
  BrandingOnboardingEnabled: string;
  TransactionApprovalWorkflowEnabled: string;
  EmailDomainEnabled: string;
  ChatSupportEnabled: boolean;
  ParentAccountID: string;
  ParentAccountName: string;
  SubAccountType: string;
  InterestAccountEnabled: boolean;
  MicroDepositBankAccountVerificationEnabled: boolean;
  InteracVerificationEnabled: boolean;
  BillPayEnabled: boolean;
  VirtualAccountsEnabled: boolean;
  AccountSummaryEnabled: boolean;
  AccountAndBillingEnabled: boolean;
  FundMyAccountEnabled: boolean;
  FundMyAccountCreditCardEnabled: boolean;
  WithdrawMyAccountEnabled: boolean;
  BankAccountTransferEnabled: boolean;
  OperationalAccountsEnabled: boolean;
  PartnerBillsAccount: boolean;
  PaymentsEnabled: boolean;
  CollectionsEnabled: boolean;
  ShowSubAccountOrgChart: boolean;
  PushToCardProcessor: vp.PushToCardProcessor | '';
  CreditCardProcessor: vp.CreditCardProcessor | '';
  ACHFundingProcessor: vp.ACHFundingProcessor | '';
  ACHWithdrawProcessor: vp.ACHWithdrawProcessor | '';
  InteracMoneyRequestProcessor: vp.InteracMoneyRequestProcessor | '';
  InteracBulkPayoutProcessor: vp.InteracBulkPayoutProcessor | '';
  WireProcessor: vp.WireProcessor | '';
  RTPProcessor: vp.RTPProcessor | '';
  FednowProcessor: vp.FednowProcessor | '';
  InteracBasicVerificationEnabled: boolean;
  InteracEnhancedVerificationEnabled: boolean;
  CardBasicVerificationEnabled: boolean;
  CardEnhancedVerificationEnabled: boolean;
  SupportTicketEnabled: boolean;
  DigitalWalletsEnabled: boolean;
  PaypalWithdrawEnabled: boolean;
  VenmoWithdrawEnabled: boolean;
  ZelleWithdrawEnabled: boolean;
  FileConversionEnabled: boolean;
  FileSubmissionEnabled: boolean;
  CPortalEnabled: boolean;
};
export type ClientDataCtx = {
  response: ClientData;
  setClientData: (ClientData: ClientData) => void;
  reset: () => void;
};

const defaults: ClientDataCtx = {
  response: {
    Success: false,
    LoginUserName: '',
    AccountID: '',
    AccountName: '',
    Key: '',
    Signature: '',
    JWT: '',
    FirebaseToken: '',
    Country: '',
    GCMEnabled: '',
    EFTCollectEnabled: '',
    EFTSendEnabled: '',
    ACHFundEnabled: '',
    ACHWithdrawEnabled: '',
    USDEFTCollectEnabled: '',
    USDEFTSendEnabled: '',
    CADCreditCardCollectionEnabled: false,
    USDCreditCardCollectionEnabled: false,
    RTRWithdrawEnabled: '',
    RTPWithdrawEnabled: '',
    WireEnabled: '',
    AFTEnabled: false,
    InteracMoneyRequestEnabled: '',
    InboundEtransferEnabled: '',
    InteracBulkPayoutEnabled: '',
    PortalViewOnly: '',
    MoneyRequestGenerateUrlEnabled: '',
    KYCLightEnabled: '',
    SubAccountPermissions: '',
    subAccountPermissions: false,
    ClientAccountsEnabled: '',
    clientAccountsEnabled: false,
    AutoBalanceTransferEnabled: '',
    TransactionLabelOverrideEnabled: '',
    TransactionResubmissionEnabled: '',
    IQ11StandardEnabled: '',
    IQ11BoostEnabled: '',
    IQ11PlusEnabled: '',
    IQ11AdvantageEnabled: '',
    IQ11DataEnrichingEnabled: false,
    IQ11DataEnrichingEnhancedEnabled: false,
    TransactionServiceDefault: '',
    PayLinkEnabled: '',
    CustomFlinksCredentialsSet: false,
    CustomPlaidCredentialsSet: false,
    CustomInveriteCredentialsSet: false,
    EnablePaylinkDecline: '',
    CreditCardEnabled: '',
    GooglePayEnabled: false,
    ApplePayEnabled: false,
    UserManagementEnabled: '',
    AccountingSoftwareIntegrationEnabled: false,
    AccountingSystemIntegrationEnabled: '',
    AccountTransferEnabled: '',
    PushToCardEnabled: '',
    RealTimeMoneyRequestEnabled: '',
    CustomGlobalPaySharedSecret: false,
    AccountType: 'Standard',
    Domain: '',
    MFAEnabled: false,
    PasswordResetRequired: false,
    BrandingEmailEnabled: '',
    BrandingELinxEnabled: '',
    BrandingPortalEnabled: '',
    BrandingOnboardingEnabled: '',
    TransactionApprovalWorkflowEnabled: '',
    EmailDomainEnabled: '0',
    ChatSupportEnabled: false,
    ParentAccountID: '',
    ParentAccountName: '',
    SubAccountType: '',
    InterestAccountEnabled: false,
    MicroDepositBankAccountVerificationEnabled: false,
    InteracVerificationEnabled: false,
    BillPayEnabled: false,
    VirtualAccountsEnabled: false,
    AccountSummaryEnabled: false,
    AccountAndBillingEnabled: false,
    FundMyAccountEnabled: false,
    FundMyAccountCreditCardEnabled: false,
    WithdrawMyAccountEnabled: false,
    BankAccountTransferEnabled: false,
    OperationalAccountsEnabled: false,
    PartnerBillsAccount: false,
    PaymentsEnabled: false,
    CollectionsEnabled: false,
    ShowSubAccountOrgChart: false,
    PushToCardProcessor: '',
    CreditCardProcessor: '',
    ACHFundingProcessor: '',
    ACHWithdrawProcessor: '',
    InteracMoneyRequestProcessor: '',
    InteracBulkPayoutProcessor: '',
    WireProcessor: '',
    RTPProcessor: '',
    FednowProcessor: '',
    InteracBasicVerificationEnabled: false,
    InteracEnhancedVerificationEnabled: false,
    CardBasicVerificationEnabled: false,
    CardEnhancedVerificationEnabled: false,
    SupportTicketEnabled: false,
    DigitalWalletsEnabled: false,
    PaypalWithdrawEnabled: false,
    VenmoWithdrawEnabled: false,
    ZelleWithdrawEnabled: false,
    FileConversionEnabled: false,
    FileSubmissionEnabled: false,
    CPortalEnabled: false,
  },
  setClientData: () => {},
  reset: () => {},
};

export const ClientDataContext = createContext<ClientDataCtx>(defaults);

export const ClientDataProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [ClientData, setClientData] = useState<ClientData>(defaults.response);

  return (
    <ClientDataContext.Provider
      value={{
        response: ClientData,
        setClientData,
        reset: () =>
          setClientData({
            Success: false,
            LoginUserName: '',
            AccountID: '',
            AccountName: '',
            Key: '',
            Signature: '',
            JWT: '',
            FirebaseToken: '',
            Country: '',
            GCMEnabled: '',
            EFTCollectEnabled: '',
            EFTSendEnabled: '',
            ACHFundEnabled: '',
            ACHWithdrawEnabled: '',
            USDEFTCollectEnabled: '',
            USDEFTSendEnabled: '',
            CADCreditCardCollectionEnabled: false,
            USDCreditCardCollectionEnabled: false,
            RTRWithdrawEnabled: '',
            RTPWithdrawEnabled: '',
            WireEnabled: '',
            AFTEnabled: false,
            InteracMoneyRequestEnabled: '',
            InboundEtransferEnabled: '',
            InteracBulkPayoutEnabled: '',
            PortalViewOnly: '',
            MoneyRequestGenerateUrlEnabled: '',
            KYCLightEnabled: '',
            SubAccountPermissions: '',
            subAccountPermissions: false,
            ClientAccountsEnabled: '',
            clientAccountsEnabled: false,
            AutoBalanceTransferEnabled: '',
            TransactionLabelOverrideEnabled: '',
            TransactionResubmissionEnabled: '',
            IQ11StandardEnabled: '',
            IQ11BoostEnabled: '',
            IQ11PlusEnabled: '',
            IQ11AdvantageEnabled: '',
            IQ11DataEnrichingEnabled: false,
            IQ11DataEnrichingEnhancedEnabled: false,
            TransactionServiceDefault: '',
            PayLinkEnabled: '',
            CustomFlinksCredentialsSet: false,
            CustomPlaidCredentialsSet: false,
            CustomInveriteCredentialsSet: false,
            EnablePaylinkDecline: '',
            CreditCardEnabled: '',
            GooglePayEnabled: false,
            ApplePayEnabled: false,
            UserManagementEnabled: '',
            AccountingSoftwareIntegrationEnabled: false,
            AccountingSystemIntegrationEnabled: '',
            AccountTransferEnabled: '',
            PushToCardEnabled: '',
            RealTimeMoneyRequestEnabled: '',
            CustomGlobalPaySharedSecret: false,
            AccountType: 'Standard',
            Domain: '',
            MFAEnabled: false,
            PasswordResetRequired: false,
            BrandingEmailEnabled: '',
            BrandingELinxEnabled: '',
            BrandingPortalEnabled: '',
            BrandingOnboardingEnabled: '',
            TransactionApprovalWorkflowEnabled: '',
            EmailDomainEnabled: '0',
            ChatSupportEnabled: false,
            ParentAccountID: '',
            ParentAccountName: '',
            SubAccountType: '',
            InterestAccountEnabled: false,
            MicroDepositBankAccountVerificationEnabled: false,
            InteracVerificationEnabled: false,
            BillPayEnabled: false,
            VirtualAccountsEnabled: false,
            AccountSummaryEnabled: false,
            AccountAndBillingEnabled: false,
            FundMyAccountEnabled: false,
            FundMyAccountCreditCardEnabled: false,
            WithdrawMyAccountEnabled: false,
            BankAccountTransferEnabled: false,
            OperationalAccountsEnabled: false,
            PartnerBillsAccount: false,
            PaymentsEnabled: false,
            CollectionsEnabled: false,
            ShowSubAccountOrgChart: false,
            PushToCardProcessor: '',
            CreditCardProcessor: '',
            ACHFundingProcessor: '',
            ACHWithdrawProcessor: '',
            InteracMoneyRequestProcessor: '',
            InteracBulkPayoutProcessor: '',
            WireProcessor: '',
            RTPProcessor: '',
            FednowProcessor: '',
            InteracBasicVerificationEnabled: false,
            InteracEnhancedVerificationEnabled: false,
            CardBasicVerificationEnabled: false,
            CardEnhancedVerificationEnabled: false,
            SupportTicketEnabled: false,
            DigitalWalletsEnabled: false,
            PaypalWithdrawEnabled: false,
            VenmoWithdrawEnabled: false,
            ZelleWithdrawEnabled: false,
            FileConversionEnabled: false,
            FileSubmissionEnabled: false,
            CPortalEnabled: false,
          }),
      }}
    >
      {children}
    </ClientDataContext.Provider>
  );
};

export const useClientData = (): ClientDataCtx => useContext(ClientDataContext);
